import React from "react";
import Avatar from "../common/Avatar";
import FifaVerifiedBadge from "../common/FifaVerifiedBadge";
import useTranslate from "../../hooks/useTranslate";
import shortRelativeTime from "./relativeTime";
import { ChatIcon } from "./LoungeFeedStates";
import LoungeKebabMenu from "./LoungeKebabMenu";
import LoungeAttachments from "./LoungeAttachments";

// Lounge post card — frames 1a (desktop), 1e (402px), 1l (360px).
//
// Scope notes:
//   * T5 wired the vote pill: both segments are real <button>s with
//     aria-pressed; the page owns the optimistic state (score + my_vote)
//     and passes `onVote(value)` down.
//   * T6 added the kebab ("…") menu — desktop in the header row (1a/1m·1),
//     mobile in the footer actions row (1k·3) — plus the edited stamps:
//     header "· edited" (1m·3) and the desktop footer "Edited {t} ago"
//     (the mobile footer is never drawn with a stamp; the header stamp
//     carries it there so 360px never overflows — declared decision).
//   * T7 draws `attachments` between text and actions row: image grid OR
//     PDF row (LoungeAttachments — GATE0 §5 Twitter grid / 1a PDF row).
//   * The channel tag hides while that channel is the active filter (1n rule).
//   * The FIFA badge gates on the CONTRACT field `author.is_fifa_verified`
//     (bool) — isFifaVerified() reads license fields this payload doesn't
//     carry, so it is deliberately not used here.

export const UpChevron = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M3.5 9.5L8 5l4.5 4.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const DownChevron = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M3.5 6.5L8 11l4.5-4.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Interactive vote pill (T5) — score = max(0, up − down), server-capped.
// Exported for the T4 detail page — the detail post draws the same
// post-sized pill (30px desktop / 44px mobile, frames 1c/1e·2/1l·2).
//
// States, straight from the design (Agent Lounge.dc.html):
//   * neutral up   1a Z.96:  text #6B7280, hover bg #F4F8FD + text #2283FF
//   * neutral down 1a Z.98:  text #9CA3AF, hover bg #F4F8FD + text #111827
//   * my_vote=1    1c Z.367: left segment bg #EDF4FF, text #2283FF
//   * my_vote=-1 — NOT drawn anywhere (design gap L16). Fill: the neutral
//     pressed look from EXISTING tokens — bg #F1F4F8 (YouChip/hover chip
//     ground) + text #111827 — no new color invented.
// `onVote(value)` with value ∈ {1,-1}; the pill itself holds no state.
export const VotePill = ({ score, myVote = null, onVote }) => {
  const { t } = useTranslate();
  const up = myVote === 1;
  const down = myVote === -1;
  return (
    <div
      data-testid="vote-pill"
      className="inline-flex items-center h-11 sm:h-[30px] border border-[#E4E9F0] rounded-full bg-white select-none overflow-hidden"
    >
      <button
        type="button"
        onClick={() => onVote && onVote(1)}
        aria-pressed={up}
        aria-label={t("Upvote")}
        className={`flex items-center gap-1.5 h-full pl-3.5 pr-2.5 sm:pl-[11px] sm:pr-2 text-[13.5px] sm:text-[13px] font-semibold border-none rounded-l-full cursor-pointer ${
          up
            ? "bg-[#EDF4FF] text-[#2283FF]"
            : "bg-transparent text-[#6B7280] hover:bg-[#F4F8FD] hover:text-[#2283FF]"
        }`}
      >
        <UpChevron />
        <span>{score}</span>
      </button>
      <span className="w-px h-4 sm:h-[15px] bg-[#E4E9F0] shrink-0" />
      <button
        type="button"
        onClick={() => onVote && onVote(-1)}
        aria-pressed={down}
        aria-label={t("Downvote")}
        className={`flex items-center h-full px-3.5 sm:px-2.5 border-none rounded-r-full cursor-pointer ${
          down
            ? "bg-[#F1F4F8] text-[#111827]"
            : "bg-transparent text-[#9CA3AF] hover:bg-[#F4F8FD] hover:text-[#111827]"
        }`}
      >
        <DownChevron />
      </button>
    </div>
  );
};

export const YouChip = ({ label }) => (
  <span className="text-[9.5px] sm:text-[10.5px] font-bold text-[#6B7280] bg-[#F1F4F8] border border-[#E4E9F0] px-1.5 sm:px-[7px] py-px rounded-full shrink-0 leading-normal">
    {label}
  </span>
);

// One tag per header: the mobile header draws the short form where one
// exists (1e/1l), the desktop header the full name (1a).
const ChannelTag = ({ name, shortName, mobile = false }) =>
  mobile ? (
    <span className="text-[10.5px] min-[402px]:text-[11px] font-semibold text-[#2283FF] bg-[#EDF4FF] px-2 min-[402px]:px-[9px] py-[3px] rounded-full whitespace-nowrap shrink-0">
      {shortName || name}
    </span>
  ) : (
    <span className="text-[12px] font-semibold text-[#2283FF] bg-[#EDF4FF] px-2.5 py-[3px] rounded-full whitespace-nowrap shrink-0">
      {name}
    </span>
  );

// `onOpenComments` (T4): the comment counter becomes the way into the
// detail page — feed passes a navigate handler, the counter turns into a
// button (negative-margin padding keeps the touch target ≥44px on mobile
// without shifting the drawn row).
// `onVote` (T5): (value) => void with value ∈ {1,-1} — the page owns the
// optimistic score/my_vote state and the service call.
// `onEdit`/`onDelete`/`onReport` (T6): kebab actions — own content gets
// Edit + Delete, foreign gets Report; without any handler no kebab is drawn
// (pre-T6 callers render unchanged).
const LoungePostCard = ({
  post,
  currentAccountId,
  activeChannel,
  highlighted,
  onOpenComments,
  onVote,
  onEdit,
  onDelete,
  onReport,
}) => {
  const { t, tv } = useTranslate();
  const author = post?.author || {};
  const channel = post?.channel || {};
  const isOwn =
    currentAccountId != null &&
    author.account_id != null &&
    Number(author.account_id) === Number(currentAccountId);
  const showTag = channel?.slug && channel.slug !== activeChannel;
  const timeLabel = shortRelativeTime(post?.created_at);
  const commentCount = post?.comment_count ?? 0;
  const hasMenu = Boolean(onEdit || onDelete || onReport);
  const edited = Boolean(post?.edited_at);
  const editedRel = edited ? shortRelativeTime(post.edited_at) : "";
  const editedFooter =
    editedRel === "now"
      ? t("Edited just now")
      : tv("Edited {{time}} ago", { time: editedRel });
  const kebab = hasMenu && (
    <LoungeKebabMenu
      own={isOwn}
      type="post"
      onEdit={onEdit}
      onDelete={onDelete}
      onReport={onReport}
    />
  );

  return (
    <article
      data-testid="lounge-post"
      className={[
        "bg-white rounded-[14px] p-[13px] min-[402px]:p-[14px_15px] sm:p-[18px_20px] transition-shadow duration-500",
        highlighted
          ? "border border-[#2283FF] shadow-[0_0_0_3px_rgba(34,131,255,0.10)]"
          : "border border-[#E4E9F0]",
      ].join(" ")}
    >
      <div className="flex sm:gap-3">
        {/* Desktop: avatar as its own left column (1a). */}
        <div className="hidden sm:block shrink-0">
          <Avatar
            src={author.profile_picture_url}
            alt={author.name || ""}
            size={38}
            fallbackText={author.name}
          />
        </div>

        <div className="flex-1 min-w-0">
          {/* Mobile header (1e/1l): avatar inside the row, agency on line 2,
              short channel tag right. */}
          <div className="flex sm:hidden items-center gap-2">
            <div className="shrink-0">
              <Avatar
                src={author.profile_picture_url}
                alt={author.name || ""}
                size={32}
                fallbackText={author.name}
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-[5px] min-w-0">
                <span className="text-[13px] min-[402px]:text-[13.5px] font-semibold text-[#111827] whitespace-nowrap overflow-hidden text-ellipsis">
                  {author.name}
                </span>
                {author.is_fifa_verified === true && <FifaVerifiedBadge size="sm" />}
                {isOwn && <YouChip label={t("You")} />}
                <span className="text-[11.5px] min-[402px]:text-[12px] text-[#9CA3AF] whitespace-nowrap shrink-0">
                  · {timeLabel}
                  {edited && <> · {t("edited")}</>}
                </span>
              </div>
              {author.agency_name ? (
                <div className="text-[11.5px] min-[402px]:text-[12px] text-[#6B7280] whitespace-nowrap overflow-hidden text-ellipsis">
                  {author.agency_name}
                </div>
              ) : null}
            </div>
            {showTag && (
              <ChannelTag name={channel.name} shortName={channel.short_name} mobile />
            )}
          </div>

          {/* Desktop header (1a): one line, tag right. */}
          <div className="hidden sm:flex items-center gap-1.5 min-w-0">
            <span className="text-[14px] font-semibold text-[#111827] whitespace-nowrap">
              {author.name}
            </span>
            {author.is_fifa_verified === true && <FifaVerifiedBadge size="sm" />}
            {isOwn && <YouChip label={t("You")} />}
            {author.agency_name ? (
              <span className="text-[13px] text-[#6B7280] whitespace-nowrap overflow-hidden text-ellipsis min-w-0">
                · {author.agency_name}
              </span>
            ) : null}
            <span className="text-[13px] text-[#9CA3AF] whitespace-nowrap">
              · {timeLabel}
              {edited && <> · {t("edited")}</>}
            </span>
            <span className="flex-1" />
            {showTag && <ChannelTag name={channel.name} shortName={channel.short_name} />}
            {kebab && <span className="hidden sm:inline-flex">{kebab}</span>}
          </div>

          <p className="mt-2 mb-2.5 min-[402px]:mt-[9px] min-[402px]:mb-[11px] sm:mt-[7px] sm:mb-3 text-[13.5px] min-[402px]:text-[14px] leading-[1.55] text-[#111827] whitespace-pre-wrap break-words">
            {post?.text}
          </p>

          {/* T7: image grid OR PDF row between text and actions (1a). */}
          <LoungeAttachments
            attachments={post?.attachments}
            className="mb-2.5 min-[402px]:mb-[11px] sm:mb-3"
          />

          <div className="flex items-center gap-2.5 min-[402px]:gap-3 sm:gap-4">
            <VotePill
              score={post?.score ?? 0}
              myVote={post?.my_vote ?? null}
              onVote={onVote}
            />
            {onOpenComments ? (
              <button
                type="button"
                onClick={onOpenComments}
                data-testid="comment-counter"
                className="inline-flex items-center gap-[5px] sm:gap-1.5 text-[#6B7280] text-[12.5px] sm:text-[13px] bg-transparent border-none p-3 -m-3 sm:p-0 sm:m-0 cursor-pointer hover:text-[#111827]"
              >
                <ChatIcon size={14} stroke="currentColor" strokeWidth={1.4} />
                <span className="sm:hidden">{commentCount}</span>
                <span className="hidden sm:inline">
                  {tv("{{count}} comments", { count: commentCount })}
                </span>
              </button>
            ) : (
              <span className="inline-flex items-center gap-[5px] sm:gap-1.5 text-[#6B7280] text-[12.5px] sm:text-[13px]">
                <ChatIcon size={14} stroke="currentColor" strokeWidth={1.4} />
                <span className="sm:hidden">{commentCount}</span>
                <span className="hidden sm:inline">
                  {tv("{{count}} comments", { count: commentCount })}
                </span>
              </span>
            )}
            <span className="flex-1" />
            {/* Footer edited stamp — desktop only (1m·3); the mobile header
                already carries "· edited". */}
            {edited && (
              <span className="hidden sm:inline text-[12px] text-[#9CA3AF] whitespace-nowrap">
                {editedFooter}
              </span>
            )}
            {/* Mobile kebab sits in the actions row (1k·3). */}
            {kebab && <span className="sm:hidden inline-flex">{kebab}</span>}
          </div>
        </div>
      </div>
    </article>
  );
};

export default LoungePostCard;
